import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ClientLayout from '../../components/layouts/ClientLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  CreditCard, Plus, Clock, CheckCircle2, XCircle, 
  Loader2, Eye, Upload, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import PaymentMethodsManager from '../../components/PaymentMethodsManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientPayments = () => {
  const { user, token, canManagePayments, canVerifyPayments } = useAuth();
  const [myPayments, setMyPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewProofDialog, setViewProofDialog] = useState(null);
  const [verifyDialog, setVerifyDialog] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_type: 'registration',
    payment_method: 'gcash',
    reference_number: '',
    notes: ''
  });
  const [proofImage, setProofImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Check if user has Finance committee permissions
  const canManage = canManagePayments && canManagePayments();
  const canVerify = canVerifyPayments && canVerifyPayments();

  const paymentTypes = [
    { value: 'registration', label: 'Registration Fee' },
    { value: 'contribution', label: 'Contribution' },
    { value: 'event_fee', label: 'Event Fee' },
  ];

  const paymentMethods = [
    { value: 'gcash', label: 'GCash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
  ];

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const [myRes, allRes] = await Promise.all([
        axios.get(`${API_URL}/api/event/payments/my`),
        canManage ? axios.get(`${API_URL}/api/event/payments?event_id=${user?.event_id}`) : Promise.resolve({ data: [] })
      ]);
      setMyPayments(myRes.data);
      setAllPayments(allRes.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (paymentId, status) => {
    try {
      await axios.put(`${API_URL}/api/event/payments/${paymentId}/verify`, {
        status,
        rejection_reason: status === 'rejected' ? rejectionReason : null
      });
      toast.success(`Payment ${status}`);
      setVerifyDialog(null);
      setRejectionReason('');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payment');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setProofImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!proofImage) {
      toast.error('Please upload proof of payment');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/event/payments`, {
        ...newPayment,
        amount: parseFloat(newPayment.amount),
        proof_image: proofImage
      });
      
      toast.success('Payment submitted! Awaiting verification.');
      setNewPayment({
        amount: '',
        payment_type: 'registration',
        payment_method: 'gcash',
        reference_number: '',
        notes: ''
      });
      setProofImage(null);
      setCreateDialogOpen(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Verified</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="client-payments">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <CreditCard className="h-8 w-8" />
              My Payments
            </h1>
            <p className="text-gray-600 mt-1">
              Track your payment history
            </p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="submit-payment-btn">
                <Plus className="h-4 w-4 mr-2" />
                Submit Payment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-maroon">Submit Payment</DialogTitle>
                <DialogDescription>Upload your GCash/Bank transfer proof</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmitPayment} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount (₱)</Label>
                    <Input
                      type="number"
                      value={newPayment.amount}
                      onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      data-testid="payment-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment For</Label>
                    <Select 
                      value={newPayment.payment_type} 
                      onValueChange={(v) => setNewPayment({ ...newPayment, payment_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <Select 
                      value={newPayment.payment_method} 
                      onValueChange={(v) => setNewPayment({ ...newPayment, payment_method: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentMethods.map((method) => (
                          <SelectItem key={method.value} value={method.value}>
                            {method.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Reference #</Label>
                    <Input
                      value={newPayment.reference_number}
                      onChange={(e) => setNewPayment({ ...newPayment, reference_number: e.target.value })}
                      placeholder="Transaction ID"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Proof of Payment *</Label>
                  <div 
                    className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-maroon transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {proofImage ? (
                      <div>
                        <img src={proofImage} alt="Proof" className="max-h-40 mx-auto mb-2 rounded-lg" />
                        <p className="text-sm text-green-600 font-medium">Proof uploaded</p>
                      </div>
                    ) : (
                      <div>
                        <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">Click to upload screenshot</p>
                        <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-maroon hover:bg-maroon-hover text-white" 
                    disabled={submitting}
                  >
                    {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {submitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Available Payment Methods (Read-only for attendees) */}
        {user?.event_id && (
          <PaymentMethodsManager 
            eventId={user.event_id} 
            token={token} 
            isOrganizer={false} 
            readOnly={true}
          />
        )}

        {/* Payment List */}
        {myPayments.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-12 text-center">
              <CreditCard className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No payments yet</h3>
              <p className="text-gray-400 mt-1">Submit your first payment to secure your spot!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {myPayments.map((payment) => (
              <Card 
                key={payment.id} 
                className={`border-0 shadow-sm transition-all ${
                  payment.status === 'pending' ? 'bg-amber-50' : 'bg-white'
                }`}
                data-testid={`payment-card-${payment.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${
                        payment.status === 'verified' ? 'bg-green-100' :
                        payment.status === 'rejected' ? 'bg-red-100' :
                        'bg-amber-100'
                      }`}>
                        <CreditCard className={`h-6 w-6 ${
                          payment.status === 'verified' ? 'text-green-600' :
                          payment.status === 'rejected' ? 'text-red-600' :
                          'text-amber-600'
                        }`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(payment.amount)}</p>
                        <p className="text-sm text-gray-500">
                          {paymentTypes.find(t => t.value === payment.payment_type)?.label} • {paymentMethods.find(m => m.value === payment.payment_method)?.label}
                        </p>
                        {payment.reference_number && (
                          <p className="text-xs text-gray-400 mt-1">Ref: {payment.reference_number}</p>
                        )}
                        <p className="text-xs text-gray-400">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(payment.status)}
                      {payment.proof_image && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setViewProofDialog(payment)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Proof
                        </Button>
                      )}
                    </div>
                  </div>
                  {payment.status === 'rejected' && payment.rejection_reason && (
                    <div className="mt-4 p-3 bg-red-50 rounded-lg flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                        <p className="text-sm text-red-600">{payment.rejection_reason}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Proof Dialog */}
        <Dialog open={!!viewProofDialog} onOpenChange={() => setViewProofDialog(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-maroon">Payment Proof</DialogTitle>
            </DialogHeader>
            {viewProofDialog?.proof_image && (
              <img 
                src={viewProofDialog.proof_image} 
                alt="Payment Proof" 
                className="w-full rounded-lg"
              />
            )}
          </DialogContent>
        </Dialog>

        {/* All Payments Section - For Finance Committee Members */}
        {canManage && (
          <Card className="border-0 shadow-sm bg-white mt-6" data-testid="all-payments-section">
            <CardHeader>
              <CardTitle className="font-heading text-xl flex items-center gap-2">
                <Badge className="bg-gold text-gray-900">Finance Committee</Badge>
                All Event Payments
              </CardTitle>
              <CardDescription>Manage and verify payments from all attendees</CardDescription>
            </CardHeader>
            <CardContent>
              {allPayments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No payments submitted yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allPayments.map((payment) => (
                    <div 
                      key={payment.id}
                      className={`flex items-center justify-between p-4 rounded-xl border ${
                        payment.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'
                      }`}
                      data-testid={`all-payment-${payment.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-white rounded-lg shadow-sm">
                          <CreditCard className="h-5 w-5 text-maroon" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{payment.user_name}</p>
                          <p className="text-lg font-bold text-maroon">{formatCurrency(payment.amount)}</p>
                          <p className="text-sm text-gray-500">
                            {paymentTypes.find(t => t.value === payment.payment_type)?.label} • {paymentMethods.find(m => m.value === payment.payment_method)?.label}
                          </p>
                          {payment.reference_number && (
                            <p className="text-xs text-gray-400">Ref: {payment.reference_number}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(payment.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(payment.status)}
                        {payment.proof_image && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setViewProofDialog(payment)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Proof
                          </Button>
                        )}
                        {payment.status === 'pending' && canVerify && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleVerify(payment.id, 'verified')}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              data-testid={`verify-btn-${payment.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Verify
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setVerifyDialog(payment)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`reject-btn-${payment.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Reject Dialog */}
        <Dialog open={!!verifyDialog} onOpenChange={() => setVerifyDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-maroon">Reject Payment</DialogTitle>
              <DialogDescription>Provide a reason for rejection</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rejection Reason</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="e.g., Invalid reference number, amount mismatch..."
                  data-testid="rejection-reason-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setVerifyDialog(null)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleVerify(verifyDialog?.id, 'rejected')}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-reject-btn"
                >
                  Reject Payment
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </ClientLayout>
  );
};

export default ClientPayments;
